import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaStar, FaUsers, FaMapMarkerAlt, FaArrowRight, FaChevronLeft, FaChevronRight } from 'react-icons/fa';

const testimonials = [
  { name: 'Priya Sharma',   location: 'Pune',       text: "Dr. Naik diagnosed my mother's rare neurological condition when others couldn't. His knowledge and compassion are truly exceptional. We are forever grateful for the care we received.", initials: 'PS', rating: 5, condition: 'Rare Neurological Disorder' },
  { name: 'Rajesh Patil',   location: 'Nashik',     text: 'I had been suffering from severe migraines for years. After just two consultations with Dr. Naik, I finally have a treatment plan that actually works. Absolutely life-changing experience.', initials: 'RP', rating: 5, condition: 'Chronic Migraine' },
  { name: 'Sneha Deshmukh', location: 'Pune',       text: "My father had a stroke and Dr. Naik's quick and precise treatment helped him recover faster than expected. He explained everything clearly to our entire family throughout the process.", initials: 'SD', rating: 5, condition: 'Stroke Recovery' },
  { name: 'Amit Kulkarni',  location: 'Aurangabad', text: "Came to Dr. Naik for Parkinson's treatment. His expertise from AIIMS really shows — the care here is at a completely different level than what we had experienced before. Highly recommend.", initials: 'AK', rating: 5, condition: "Parkinson's Disease" },
  { name: 'Meera Joshi',    location: 'Pune',       text: 'Excellent doctor with tremendous patience. He took time to explain my epilepsy diagnosis thoroughly and gave us a clear long-term management plan. Our family feels confident and informed.', initials: 'MJ', rating: 5, condition: 'Epilepsy Management' },
  { name: 'Suresh Nair',    location: 'Mumbai',     text: 'Travelled from Mumbai specifically to see Dr. Naik for my neuropathy. Worth every bit — his diagnosis was spot-on and the treatment has shown great improvement within weeks.', initials: 'SN', rating: 5, condition: 'Neuropathy' },
];

const StarRow = ({ count }) => (
  <div style={{ display: 'flex', gap: 3, color: 'var(--amber)' }}>
    {Array.from({ length: count }).map((_, i) => <FaStar key={i} size={13} />)}
  </div>
);

const Testimonials = () => {
  const navigate = useNavigate();
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState('next'); // 'next' or 'prev'
  const [animating, setAnimating] = useState(false);
  const autoPlayRef = useRef(null);
  const total = testimonials.length;

  const visible = [
    testimonials[current % total],
    testimonials[(current + 1) % total],
    testimonials[(current + 2) % total],
  ];

  const goNext = () => {
    if (animating) return;
    setDirection('next');
    setAnimating(true);
    setTimeout(() => {
      setCurrent(c => (c + 1) % total);
      setAnimating(false);
    }, 400);
  };

  const goPrev = () => {
    if (animating) return;
    setDirection('prev');
    setAnimating(true);
    setTimeout(() => {
      setCurrent(c => (c - 1 + total) % total);
      setAnimating(false);
    }, 400);
  };

  const goTo = (i) => {
    if (animating || i === current) return;
    setDirection(i > current ? 'next' : 'prev');
    setAnimating(true);
    setTimeout(() => {
      setCurrent(i);
      setAnimating(false);
    }, 400);
  };

  // Auto-scroll every 2 seconds
  useEffect(() => {
    autoPlayRef.current = setInterval(() => {
      goNext();
    }, 2000);
    return () => clearInterval(autoPlayRef.current);
  }, [animating]);

  return (
    <div className="testimonials-page">

      {/* ── PAGE HERO ── */}
      <section className="page-hero">
        <div className="page-hero-bg-dots" />
        <div className="container">
          <div className="page-hero-content">
            <div className="section-tag">Patient Stories</div>
            <h1 className="page-hero-title">What Our Patients Say</h1>
            <p className="page-hero-sub">
              Real stories from real patients — lives changed through expert neurological care at Naik Neuro Clinic.
            </p>
          </div>
        </div>
      </section>

      {/* ── TRUST NUMBERS ── */}
      <section className="testimonials-trust-bar">
        <div className="container">
          <div className="testimonials-trust-grid">
            {[
              { icon: <FaUsers size={22} />,       num: '5000+',   label: 'Patients Treated' },
              { icon: <FaStar size={20} />,         num: '5★',      label: 'Average Rating' },
              { icon: <FaMapMarkerAlt size={20} />, num: 'Nashik', label: 'Pan-Maharashtra Patients' },
              { icon: <FaArrowRight size={18} />,   num: '10+',     label: 'Years of Clinical Practice' },
            ].map(({ icon, num, label }) => (
              <div key={label} className="testimonials-trust-item">
                <div className="testimonials-trust-icon">{icon}</div>
                <div className="testimonials-trust-num">{num}</div>
                <div className="testimonials-trust-label">{label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CAROUSEL ── */}
      <section className="testimonials-all-section">
        <div className="container">
          <div className="testimonials-section">
            <div className="testimonials-carousel">
              <button className="carousel-btn" onClick={goPrev} aria-label="Previous">
                <FaChevronLeft size={16} />
              </button>

              <div
                className="testimonials-grid"
                style={{
                  overflow: 'hidden',
                  animation: animating
                    ? direction === 'next'
                      ? 'slideInFromRight 0.4s ease forwards'
                      : 'slideInFromLeft 0.4s ease forwards'
                    : 'none',
                }}
              >
                {visible.map((t, i) => (
                  <div
                    key={`${current}-${i}`}
                    className={`testimonial-card ${i === 1 ? 'testimonial-card-center' : ''}`}
                  >
                    <StarRow count={t.rating} />
                    <p className="testimonial-text">"{t.text}"</p>
                    <div className="testimonial-condition-tag">{t.condition}</div>
                    <div className="testimonial-author-row">
                      <div className="testimonial-avatar">{t.initials}</div>
                      <div>
                        <div className="testimonial-name">{t.name}</div>
                        <div className="testimonial-loc">
                          <FaMapMarkerAlt size={11} style={{ marginRight: 4 }} />{t.location}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <button className="carousel-btn" onClick={goNext} aria-label="Next">
                <FaChevronRight size={16} />
              </button>
            </div>

            <div className="carousel-dots">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  className={`carousel-dot ${i === current ? 'active' : ''}`}
                  onClick={() => goTo(i)}
                />
              ))}
            </div>
          </div>
        </div>
      </section>




    </div>
  );
};

export default Testimonials;